import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Trophy, Check, RotateCcw, Lock, Calendar, Clock, MapPin, MessageSquare, Edit2, Crown, Share2, Copy, CheckCircle, BarChart3 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useTournament } from "../context/TournamentContext";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import ExportButton from "../components/ExportButton";
import LanguageSelector from "../components/LanguageSelector";
import MatchPlayerStats from "../components/MatchPlayerStats";

export default function LeaguePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentTournament, loadTournament, updateLeagueScore, updateMatchSchedule, resetMatch, isCreator } = useTournament();
  const { t } = useLanguage();
  const leagueRef = useRef(null);

  const [selectedMatch, setSelectedMatch] = useState(null);
  const [scoreA, setScoreA] = useState("");
  const [scoreB, setScoreB] = useState("");
  const [matchStats, setMatchStats] = useState([]);
  const [statsValid, setStatsValid] = useState(true);
  const handleStatsChange = useCallback((combined, valid) => {
    setMatchStats(combined);
    setStatsValid(valid);
  }, []);
  
  // Schedule editing
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [scheduleLocation, setScheduleLocation] = useState("");
  const [scheduleNotes, setScheduleNotes] = useState("");

  // Share functionality
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Generate share link
  const shareLink = typeof window !== 'undefined' ? `${window.location.origin}/t/${id}` : '';

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setLinkCopied(true);
      toast.success(t('toast.linkCopied'));
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      toast.error(t('toast.linkCopyFailed'));
    }
  };

  useEffect(() => {
    if (id) {
      const loadData = async () => {
        const tournament = await loadTournament(id);
        if (!tournament) {
          toast.error(t('toast.tournamentNotFound'));
          navigate("/");
        }
      };
      loadData();
    }
  }, [id, loadTournament, navigate]);

  // Remove auto-redirect - allow viewing completed tournaments

  if (!currentTournament) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-white/60">{t('loading')}</div>
      </div>
    );
  }

  const { matches, standings, champion } = currentTournament;
  const hasPlayerStats = currentTournament.hasPlayerStats;
  const completedMatches = matches?.filter((m) => m.status === "completed") || [];
  const pendingMatches = matches?.filter((m) => m.status === "pending") || [];
  
  // Group pending matches by round
  const matchesByRound = {};
  pendingMatches.forEach(match => {
    const round = match.round || 1;
    if (!matchesByRound[round]) matchesByRound[round] = [];
    matchesByRound[round].push(match);
  });

  const openScoreDialog = (match) => {
    if (!isCreator) {
      toast.info(t('toast.viewOnly'));
      return;
    }
    setSelectedMatch(match);
    setScoreA(match.scoreA !== null ? String(match.scoreA) : "");
    setScoreB(match.scoreB !== null ? String(match.scoreB) : "");
  };

  const handleSubmitScore = () => {
    if (!selectedMatch) return;
    if (!isCreator) {
      toast.error(t('toast.noPermission'));
      return;
    }

    // Default empty scores to 0
    const numScoreA = scoreA === "" ? 0 : parseInt(scoreA, 10);
    const numScoreB = scoreB === "" ? 0 : parseInt(scoreB, 10);

    if (isNaN(numScoreA) || isNaN(numScoreB)) {
      toast.error(t('toast.enterValidScores'));
      return;
    }

    if (numScoreA < 0 || numScoreB < 0) {
      toast.error(t('toast.scoresCannotBeNegative'));
      return;
    }

    if (hasPlayerStats && !statsValid) {
      toast.error(t('stats.goalsMustEqualScore'));
      return;
    }

    updateLeagueScore(selectedMatch.id, numScoreA, numScoreB, hasPlayerStats ? matchStats : null);
    setSelectedMatch(null);
    setScoreA("");
    setScoreB("");
    setMatchStats([]);
    setStatsValid(true);
    toast.success(t('toast.scoreRecorded'));
  };

  const handleResetMatch = (match, e) => {
    e.stopPropagation();
    if (!isCreator) {
      toast.info(t('toast.viewOnlyReset'));
      return;
    }
    resetMatch(match.id);
    toast.success(t('toast.matchReset'));
  };

  const openScheduleDialog = (match, e) => {
    e.stopPropagation();
    // Only creators can edit schedule
    if (!isCreator) {
      toast.info(t('toast.viewOnlySchedule'));
      return;
    }
    setEditingSchedule(match);
    setScheduleDate(match.scheduledDate || "");
    setScheduleTime(match.scheduledTime || "");
    setScheduleLocation(match.location || "");
    setScheduleNotes(match.notes || "");
  };

  const handleSaveSchedule = () => {
    // Double check creator permission
    if (!editingSchedule || !isCreator) {
      toast.error(t('toast.noSchedulePermission'));
      return;
    }

    updateMatchSchedule(editingSchedule.id, {
      scheduledDate: scheduleDate,
      scheduledTime: scheduleTime,
      location: scheduleLocation,
      notes: scheduleNotes,
    });

    setEditingSchedule(null);
    toast.success(t('toast.scheduleUpdated'));
  };

  const formatScheduleInfo = (match) => {
    const parts = [];
    if (match.scheduledDate) parts.push(match.scheduledDate);
    if (match.scheduledTime) parts.push(match.scheduledTime);
    if (match.location) parts.push(match.location);
    return parts.join(" • ");
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/30 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
              <Button
                data-testid="back-to-home-btn"
                variant="ghost"
                size="icon"
                onClick={() => navigate("/")}
                className="text-white/60 hover:text-white hover:bg-white/10 flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="min-w-0">
                <h1
                  className="font-heading text-base sm:text-xl font-bold tracking-tight text-white uppercase truncate"
                  data-testid="tournament-title"
                >
                  {currentTournament.name}
                </h1>
                <p className="text-white/40 text-xs sm:text-sm font-data hidden sm:block">
                  {currentTournament.sportType} • {currentTournament.participants?.length} {t('home.teams')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <LanguageSelector />
              {hasPlayerStats && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/stats/${currentTournament.id}`)}
                  className="border-neon-purple/30 text-neon-purple hover:bg-neon-purple/10 gap-2"
                  data-testid="player-stats-btn"
                >
                  <BarChart3 className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('stats.playerStats')}</span>
                </Button>
              )}
              {isCreator && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/setup?edit=${currentTournament.id}`)}
                  className="border-white/20 text-white/80 hover:bg-white/10 gap-2"
                  data-testid="edit-tournament-btn"
                >
                  <Edit2 className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('setup.editTournament')}</span>
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowShareDialog(true)}
                className="border-neon-cyan/30 text-neon-cyan hover:bg-neon-cyan/10 gap-2"
                data-testid="share-tournament-btn"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">{t('share')}</span>
              </Button>
              <ExportButton 
                targetRef={leagueRef} 
                filename={`${currentTournament.name}-league`}
                title={t('export')}
              />
              <Badge
                onClick={() => {
                  navigator.clipboard?.writeText(currentTournament.code);
                  toast.success(t('toast.codeCopied'));
                }}
                data-testid="copy-code-badge"
                title={t('toast.codeCopied')}
                className="bg-white/10 text-white/60 border-white/20 font-data text-xs flex flex-shrink-0 items-center gap-1 cursor-pointer hover:bg-white/20 hover:text-white transition-colors"
              >
                {currentTournament.code}
                <Copy className="w-3 h-3" />
              </Badge>
              {!isCreator && (
                <Badge data-testid="view-only-badge" className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 font-data flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  <span className="hidden sm:inline">{t('bracket.viewOnly')}</span>
                </Badge>
              )}
              {champion && (
                <Badge className="bg-neon-green/20 text-neon-green border-neon-green/30 flex items-center gap-1">
                  <Crown className="w-3 h-3" />
                  <span className="hidden sm:inline">{t('home.completed')}</span>
                </Badge>
              )}
              <div className="hidden sm:flex items-center gap-2 text-white/60 text-sm">
                <Trophy className="w-4 h-4 text-neon-cyan" />
                <span className="font-data">{t('setup.league')}</span>
              </div>
              {champion && (
                <Button
                  onClick={() => navigate(`/champion/${currentTournament.id}`)}
                  className="bg-neon-green/20 text-neon-green hover:bg-neon-green/30 border border-neon-green/30"
                  size="sm"
                >
                  <Trophy className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">{t('league.viewChampion')}</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main ref={leagueRef} className="container mx-auto px-3 sm:px-6 py-4 sm:py-8">
        <Tabs defaultValue="standings" className="w-full">
          <TabsList className="bg-white/5 border border-white/10 mb-4 sm:mb-6">
            <TabsTrigger
              value="standings"
              data-testid="standings-tab"
              className="data-[state=active]:bg-neon-cyan data-[state=active]:text-black font-heading uppercase text-xs sm:text-sm"
            >
              {t('league.standings')}
            </TabsTrigger>
            <TabsTrigger
              value="matches"
              data-testid="matches-tab"
              className="data-[state=active]:bg-neon-cyan data-[state=active]:text-black font-heading uppercase text-xs sm:text-sm"
            >
              {t('league.matches')} ({pendingMatches.length})
            </TabsTrigger>
          </TabsList>

          {/* Standings Tab */}
          <TabsContent value="standings">
            <div className="card-surface overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/60 font-heading uppercase">#</TableHead>
                    <TableHead className="text-white/60 font-heading uppercase">{t('bracket.player')}</TableHead>
                    <TableHead className="text-white/60 font-heading uppercase text-center">{t('league.played')}</TableHead>
                    <TableHead className="text-white/60 font-heading uppercase text-center">{t('league.won')}</TableHead>
                    <TableHead className="text-white/60 font-heading uppercase text-center">{t('league.drawn')}</TableHead>
                    <TableHead className="text-white/60 font-heading uppercase text-center">{t('league.lost')}</TableHead>
                    <TableHead className="text-white/60 font-heading uppercase text-center hidden sm:table-cell">{t('league.goalsFor')}</TableHead>
                    <TableHead className="text-white/60 font-heading uppercase text-center hidden sm:table-cell">{t('league.goalsAgainst')}</TableHead>
                    <TableHead className="text-white/60 font-heading uppercase text-center">{t('league.goalDiff')}</TableHead>
                    <TableHead className="text-white/60 font-heading uppercase text-center">{t('bracket.points')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {standings?.map((team, index) => (
                    <TableRow
                      key={team.id}
                      className={`border-white/5 ${index === 0 ? "bg-neon-green/5" : ""}`}
                      data-testid={`standing-row-${team.id}`}
                    >
                      <TableCell className="font-data">
                        <span className={index === 0 ? "text-neon-green font-bold" : "text-white/60"}>
                          {index + 1}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {team.logo ? (
                            <img src={team.logo} alt="" className="w-6 h-6 rounded object-cover flex-shrink-0" />
                          ) : index === 0 ? (
                            <Trophy className="w-4 h-4 text-neon-green" />
                          ) : null}
                          <span className={`font-heading uppercase ${index === 0 ? "text-neon-green font-bold" : "text-white"}`}>
                            {team.name}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center text-white/60 font-data">{team.played}</TableCell>
                      <TableCell className="text-center text-neon-green font-data">{team.wins}</TableCell>
                      <TableCell className="text-center text-yellow-400 font-data">{team.draws}</TableCell>
                      <TableCell className="text-center text-red-400 font-data">{team.losses}</TableCell>
                      <TableCell className="text-center text-white/60 font-data hidden sm:table-cell">{team.goalsFor}</TableCell>
                      <TableCell className="text-center text-white/60 font-data hidden sm:table-cell">{team.goalsAgainst}</TableCell>
                      <TableCell className="text-center font-data">
                        <span className={team.goalDiff > 0 ? "text-neon-green" : team.goalDiff < 0 ? "text-red-400" : "text-white/60"}>
                          {team.goalDiff > 0 ? "+" : ""}{team.goalDiff}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={`font-data font-bold ${index === 0 ? "text-neon-green" : "text-white"}`}>
                          {team.points}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Matches Tab */}
          <TabsContent value="matches">
            <div className="space-y-8">
              {/* Pending Matches by Round */}
              {Object.keys(matchesByRound).length > 0 && (
                <div className="space-y-6">
                  {Object.entries(matchesByRound).map(([round, roundMatches]) => (
                    <div key={round}>
                      <h3 className="font-heading text-lg uppercase tracking-wide text-neon-cyan mb-4">
                        {t('league.round')} {round}
                      </h3>
                      <div className="grid gap-3">
                        {roundMatches.map((match) => (
                          <div
                            key={match.id}
                            className="card-surface p-4 transition-all"
                            data-testid={`match-card-${match.id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div 
                                className={`flex items-center gap-4 flex-1 ${isCreator ? "cursor-pointer" : "cursor-default"}`}
                                onClick={() => isCreator && openScoreDialog(match)}
                              >
                                <div className="flex items-center gap-2">
                                  {match.participantA?.logo && (
                                    <img src={match.participantA.logo} alt="" className="w-6 h-6 rounded object-cover" />
                                  )}
                                  <span className="font-heading uppercase text-white">{match.participantA?.name}</span>
                                </div>
                                <span className="text-white/30 font-heading">VS</span>
                                <div className="flex items-center gap-2">
                                  {match.participantB?.logo && (
                                    <img src={match.participantB.logo} alt="" className="w-6 h-6 rounded object-cover" />
                                  )}
                                  <span className="font-heading uppercase text-white">{match.participantB?.name}</span>
                                </div>
                              </div>
                              {/* Only show edit button and pulse indicator for creators */}
                              {isCreator && (
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={(e) => openScheduleDialog(match, e)}
                                    className="text-white/40 hover:text-neon-cyan hover:bg-neon-cyan/10"
                                    data-testid={`schedule-match-${match.id}`}
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  <div className="w-2 h-2 bg-neon-cyan rounded-full animate-pulse" />
                                </div>
                              )}
                            </div>
                            
                            {/* Schedule Info */}
                            {(match.scheduledDate || match.scheduledTime || match.location || match.notes) && (
                              <div className="mt-3 pt-3 border-t border-white/10 space-y-1">
                                {(match.scheduledDate || match.scheduledTime) && (
                                  <div className="flex items-center gap-2 text-white/50 text-sm">
                                    <Calendar className="w-3 h-3" />
                                    <span>{match.scheduledDate}</span>
                                    {match.scheduledTime && (
                                      <>
                                        <Clock className="w-3 h-3 ml-2" />
                                        <span>{match.scheduledTime}</span>
                                      </>
                                    )}
                                  </div>
                                )}
                                {match.location && (
                                  <div className="flex items-center gap-2 text-white/50 text-sm">
                                    <MapPin className="w-3 h-3" />
                                    <span>{match.location}</span>
                                  </div>
                                )}
                                {match.notes && (
                                  <div className="flex items-center gap-2 text-white/50 text-sm">
                                    <MessageSquare className="w-3 h-3" />
                                    <span>{match.notes}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Completed Matches */}
              {completedMatches.length > 0 && (
                <div>
                  <h3 className="font-heading text-lg uppercase tracking-wide text-white/80 mb-4">
                    {t('league.completedMatches')}
                  </h3>
                  <div className="grid gap-3">
                    {completedMatches.map((match) => (
                      <div
                        key={match.id}
                        onClick={() => isCreator && openScoreDialog(match)}
                        className={`card-surface p-4 flex items-center justify-between border-l-4 border-l-neon-green ${
                          isCreator ? "cursor-pointer" : "cursor-default"
                        }`}
                        data-testid={`match-card-${match.id}`}
                      >
                        <div className="flex items-center gap-4 flex-1">
                          <div className="flex items-center gap-2">
                            {match.participantA?.logo && (
                              <img src={match.participantA.logo} alt="" className="w-6 h-6 rounded object-cover" />
                            )}
                            <span className={`font-heading uppercase ${match.scoreA > match.scoreB ? "text-neon-green font-bold" : match.scoreA < match.scoreB ? "text-white" : "text-yellow-400"}`}>
                              {match.participantA?.name}
                            </span>
                          </div>
                          <span className="font-data text-xl font-bold text-white">
                            {match.scoreA} - {match.scoreB}
                          </span>
                          <div className="flex items-center gap-2">
                            {match.participantB?.logo && (
                              <img src={match.participantB.logo} alt="" className="w-6 h-6 rounded object-cover" />
                            )}
                            <span className={`font-heading uppercase ${match.scoreB > match.scoreA ? "text-neon-green font-bold" : match.scoreB < match.scoreA ? "text-white" : "text-yellow-400"}`}>
                              {match.participantB?.name}
                            </span>
                          </div>
                        </div>
                        {isCreator && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => handleResetMatch(match, e)}
                            className="text-white/30 hover:text-red-400 hover:bg-red-500/10"
                            data-testid={`reset-match-${match.id}`}
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Score Dialog */}
      <Dialog open={!!selectedMatch && isCreator} onOpenChange={() => setSelectedMatch(null)}>
        <DialogContent className="bg-surface-card border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase text-white">
              {t('bracket.enterScores')}
            </DialogTitle>
            <DialogDescription className="text-white/60">{t('setup.league')} {t('league.matches')}</DialogDescription>
          </DialogHeader>

          {selectedMatch && (
            <div className="space-y-6 py-4">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <p className="font-heading text-lg uppercase text-white">
                    {selectedMatch.participantA?.name}
                  </p>
                </div>
                <Input
                  data-testid="score-input-a"
                  type="number"
                  min="0"
                  value={scoreA}
                  onChange={(e) => setScoreA(e.target.value)}
                  className="w-20 input-dark text-center text-2xl font-data"
                  placeholder="0"
                />
              </div>

              <div className="text-center text-white/30 font-heading">{t('bracket.vs')}</div>

              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <p className="font-heading text-lg uppercase text-white">
                    {selectedMatch.participantB?.name}
                  </p>
                </div>
                <Input
                  data-testid="score-input-b"
                  type="number"
                  min="0"
                  value={scoreB}
                  onChange={(e) => setScoreB(e.target.value)}
                  className="w-20 input-dark text-center text-2xl font-data"
                  placeholder="0"
                />
              </div>

              {hasPlayerStats && (
                <div className="border-t border-white/10 pt-4">
                  <MatchPlayerStats
                    key={selectedMatch.id}
                    participantA={selectedMatch.participantA}
                    participantB={selectedMatch.participantB}
                    scoreA={scoreA}
                    scoreB={scoreB}
                    existing={currentTournament.playerStats?.[selectedMatch.id]}
                    onChange={handleStatsChange}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              data-testid="cancel-score-btn"
              variant="ghost"
              onClick={() => setSelectedMatch(null)}
              className="text-white/60 hover:text-white"
            >
              {t('cancel')}
            </Button>
            <Button
              data-testid="submit-score-btn"
              onClick={handleSubmitScore}
              className="bg-neon-cyan text-black font-heading uppercase tracking-wide hover:bg-neon-cyan/90"
            >
              <Check className="w-4 h-4 mr-2" />
              {t('confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={!!editingSchedule && isCreator} onOpenChange={() => setEditingSchedule(null)}>
        <DialogContent className="bg-surface-card border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase text-white">
              {t('league.matches')}
            </DialogTitle>
            <DialogDescription className="text-white/60">
              {editingSchedule?.participantA?.name} {t('bracket.vs')} {editingSchedule?.participantB?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/60 text-sm uppercase tracking-wide">{t('shareDialog.name')}</Label>
                <Input
                  data-testid="schedule-date-input"
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  className="input-dark mt-1"
                />
              </div>
              <div>
                <Label className="text-white/60 text-sm uppercase tracking-wide">Time</Label>
                <Input
                  data-testid="schedule-time-input"
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="input-dark mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-white/60 text-sm uppercase tracking-wide">Location</Label>
              <Input
                data-testid="schedule-location-input"
                value={scheduleLocation}
                onChange={(e) => setScheduleLocation(e.target.value)}
                placeholder="e.g., Gym, Field A, Court 1"
                className="input-dark mt-1"
              />
            </div>
            
            <div>
              <Label className="text-white/60 text-sm uppercase tracking-wide">Notes</Label>
              <Textarea
                data-testid="schedule-notes-input"
                value={scheduleNotes}
                onChange={(e) => setScheduleNotes(e.target.value)}
                placeholder="Additional info for players..."
                className="input-dark mt-1 min-h-[80px] resize-none"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setEditingSchedule(null)}
              className="text-white/60 hover:text-white"
            >
              {t('cancel')}
            </Button>
            <Button
              data-testid="save-schedule-btn"
              onClick={handleSaveSchedule}
              className="bg-neon-cyan text-black font-heading uppercase tracking-wide hover:bg-neon-cyan/90"
            >
              <Check className="w-4 h-4 mr-2" />
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="bg-surface-card border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase text-white flex items-center gap-2">
              <Share2 className="w-5 h-5 text-neon-cyan" />
              {t('shareDialog.title')}
            </DialogTitle>
            <DialogDescription className="text-white/60">
              {t('shareDialog.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-white/60 text-sm font-heading uppercase">{t('shareDialog.shareLink')}</label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={shareLink}
                  className="input-dark font-data text-sm"
                  data-testid="share-link-input"
                />
                <Button
                  onClick={copyShareLink}
                  className={`px-4 ${linkCopied ? 'bg-neon-green text-black' : 'bg-neon-cyan text-black'} hover:opacity-90`}
                  data-testid="copy-link-btn"
                >
                  {linkCopied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* QR Code */}
            <div className="flex justify-center py-4">
              <div className="bg-white p-3 rounded-lg">
                <QRCodeSVG value={shareLink} size={150} level="M" data-testid="share-qr-code" />
              </div>
            </div>

            <div className="border-t border-white/10 pt-4">
              <p className="text-white/40 text-xs">
                {t('shareDialog.orShare')}
              </p>
              <div className="flex gap-4 mt-2">
                <div>
                  <span className="text-white/60 text-xs">{t('shareDialog.name')}:</span>
                  <p className="text-white font-heading uppercase">{currentTournament.name}</p>
                </div>
                <div>
                  <span className="text-white/60 text-xs">{t('shareDialog.code')}:</span>
                  <p className="text-neon-cyan font-data font-bold">{currentTournament.code}</p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={() => setShowShareDialog(false)}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              {t('close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
